import {
  BigQueryUploadJobStatus,
  IBigQueryUploadJob,
  IBigQueryUploadsConfig,
  IBigQueryUploadStats,
  RootCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  asColRef,
  AtLeast,
  CollectionReference,
  DocumentReference,
  limit,
  orderBy,
  Query,
  toQuery,
  toTimestamp,
  uid,
  where,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { RootConfig } from '../root-config';

export class BigQueryUploadJob {
  static init(
    overrides: AtLeast<IBigQueryUploadJob, 'dataset' | 'table'>
  ): IBigQueryUploadJob {
    return {
      uid: uid(),
      status: BigQueryUploadJobStatus.Pending,
      expiresAt: toTimestamp(moment().add(10, 'minutes')),
      files: [],
      ...overrides,
    };
  }

  static jobsCol(): CollectionReference<IBigQueryUploadJob> {
    return asColRef<IBigQueryUploadJob>(RootCollection.BigQueryUploadJobs);
  }

  static statsCol(): CollectionReference<IBigQueryUploadStats> {
    return asColRef<IBigQueryUploadStats>(RootCollection.BigQueryUploadStats);
  }

  static config(): DocumentReference<IBigQueryUploadsConfig> {
    return RootConfig.bigQueryUploads();
  }

  static queryRecentJobs(
    fromDate: moment.Moment = moment().subtract(10, 'minutes'),
    statuses: BigQueryUploadJobStatus[] = [
      BigQueryUploadJobStatus.Pending,
      BigQueryUploadJobStatus.InProgress,
      BigQueryUploadJobStatus.Failed,
      BigQueryUploadJobStatus.Completed,
    ]
  ): Query<IBigQueryUploadJob> {
    return toQuery(
      BigQueryUploadJob.jobsCol(),
      where('createdAt', '>=', toTimestamp(fromDate)),
      where('status', 'in', statuses),
      orderBy('createdAt', 'desc')
    );
  }

  static queryActiveJobs(): Query<IBigQueryUploadJob> {
    const fromDate = moment().subtract(5, 'minutes');
    return BigQueryUploadJob.queryRecentJobs(fromDate, [
      BigQueryUploadJobStatus.Pending,
      BigQueryUploadJobStatus.InProgress,
    ]);
  }

  static queryRecentStats(
    fromDate: moment.Moment = moment().subtract(10, 'minutes')
  ): Query<IBigQueryUploadStats> {
    return toQuery(
      BigQueryUploadJob.statsCol(),
      where('evaluatedAt', '>=', toTimestamp(fromDate)),
      orderBy('evaluatedAt', 'desc')
    );
  }

  static queryLastStats(): Query<IBigQueryUploadStats> {
    return toQuery(BigQueryUploadJob.queryRecentStats(), limit(1));
  }
}
